import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Brain, Users, TrendingUp, CheckCircle, XCircle, AlertCircle,
  Mail, ChevronRight, Loader, RefreshCw, Star, Target, Award, Zap,
  UserCheck, UserX, Calendar, Clock, Filter, Download, BarChart3,
  Sparkles, Send, CheckSquare, Search, Building2, GitCompare, CalendarPlus,
  Video, Phone, MapPin, FileText, X, Trophy, ThumbsUp, ThumbsDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AIScoreDashboard = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [data, setData] = useState(null);
  const [selectedTab, setSelectedTab] = useState('all');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [inviteForm, setInviteForm] = useState({
    interview_date: '',
    interview_time: '',
    interview_duration: '60',
    interview_type: 'video',
    meeting_link: '',
    location: '',
    message: '',
    send_calendar_invite: true
  });

  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchRankedApplications();
  }, [token, navigate, jobId]);

  const fetchRankedApplications = async () => {
    try {
      const res = await axios.get(`${API}/api/company/jobs/${jobId}/applications/ranked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Job not found');
        navigate('/company/jobs');
      } else {
        toast.error('Failed to load applications');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkScore = async () => {
    setScoring(true);
    try {
      const res = await axios.post(
        `${API}/api/company/jobs/${jobId}/ai-score-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Scored ${res.data.scored_count} applications`);
      fetchRankedApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scoring failed');
    } finally {
      setScoring(false);
    }
  };

  const handleSingleScore = async (applicationId) => {
    try {
      await axios.post(
        `${API}/api/company/jobs/${jobId}/applications/${applicationId}/ai-score`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Application scored');
      fetchRankedApplications();
    } catch (err) {
      toast.error('Scoring failed');
    }
  };

  const handleAutoShortlist = async () => {
    const strongFit = data?.ranked_applications?.filter(
      a => a.ai_score?.recommendation === 'STRONG_FIT'
    ) || [];
    
    if (strongFit.length === 0) {
      toast.info('No STRONG_FIT candidates to shortlist');
      return;
    }

    try {
      for (const app of strongFit) {
        await axios.put(
          `${API}/api/company/jobs/${jobId}/applications/${app.id}/status`,
          { status: 'shortlisted' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success(`Shortlisted ${strongFit.length} top candidates`);
      fetchRankedApplications();
    } catch (err) {
      toast.error('Auto-shortlist failed');
    }
  };

  const handleSendInvites = async () => {
    if (selectedCandidates.length === 0) {
      toast.error('Select candidates to invite');
      return;
    }

    setSendingInvites(true);
    try {
      const res = await axios.post(
        `${API}/api/company/jobs/${jobId}/send-interview-invites`,
        {
          application_ids: selectedCandidates,
          ...inviteForm
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Sent ${res.data.sent_count} interview invites`);
      setShowInviteModal(false);
      setSelectedCandidates([]);
      fetchRankedApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invites');
    } finally {
      setSendingInvites(false);
    }
  };

  const handleSelectCandidate = (appId) => {
    setSelectedCandidates(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleSelectAllStrongFit = () => {
    const strongFitIds = data?.ranked_applications
      ?.filter(a => a.ai_score?.recommendation === 'STRONG_FIT')
      .map(a => a.id) || [];
    setSelectedCandidates(strongFitIds);
  };

  // AI Candidate Comparison
  const handleCompare = async () => {
    if (selectedCandidates.length < 2) {
      toast.error('Select at least 2 candidates to compare');
      return;
    }
    if (selectedCandidates.length > 4) {
      toast.error('Select maximum 4 candidates to compare');
      return;
    }

    setComparing(true);
    setShowCompareModal(true);
    setComparisonResult(null);

    try {
      const res = await axios.post(
        `${API}/api/company/jobs/${jobId}/compare-candidates`,
        { application_ids: selectedCandidates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComparisonResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Comparison failed');
      setShowCompareModal(false);
    } finally {
      setComparing(false);
    }
  };

  // Fetch scheduled interviews
  const fetchScheduledInterviews = async () => {
    try {
      const res = await axios.get(
        `${API}/api/company/jobs/${jobId}/scheduled-interviews`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScheduledInterviews(res.data.interviews || []);
    } catch (err) {
      console.error('Failed to fetch interviews:', err);
    }
  };

  // Enhanced interview scheduling with ICS
  const handleScheduleInterview = async () => {
    if (selectedCandidates.length === 0) {
      toast.error('Select candidates to schedule');
      return;
    }
    if (!inviteForm.interview_date || !inviteForm.interview_time) {
      toast.error('Please select date and time');
      return;
    }

    setSendingInvites(true);
    try {
      const res = await axios.post(
        `${API}/api/company/jobs/${jobId}/schedule-interviews`,
        {
          application_ids: selectedCandidates,
          ...inviteForm
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Scheduled ${res.data.scheduled_count} interviews with calendar invites`);
      setShowScheduleModal(false);
      setSelectedCandidates([]);
      setInviteForm({
        interview_date: '',
        interview_time: '',
        interview_duration: '60',
        interview_type: 'video',
        meeting_link: '',
        location: '',
        message: '',
        send_calendar_invite: true
      });
      fetchRankedApplications();
      fetchScheduledInterviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scheduling failed');
    } finally {
      setSendingInvites(false);
    }
  };

  // Get candidates for comparison display
  const getSelectedCandidatesData = () => {
    const all = [...(data?.ranked_applications || []), ...(data?.unscored_applications || [])];
    return selectedCandidates.map(id => all.find(a => a.id === id)).filter(Boolean);
  };

  const getRecommendationColor = (rec) => {
    switch (rec) {
      case 'STRONG_FIT': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'GOOD_FIT': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'MODERATE_FIT': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'WEAK_FIT': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-blue-400';
    if (score >= 40) return 'text-amber-400';
    return 'text-red-400';
  };

  const filteredApplications = () => {
    if (!data) return [];
    const all = [...(data.ranked_applications || []), ...(data.unscored_applications || [])];
    
    switch (selectedTab) {
      case 'strong':
        return data.ranked_applications?.filter(a => a.ai_score?.recommendation === 'STRONG_FIT') || [];
      case 'good':
        return data.ranked_applications?.filter(a => a.ai_score?.recommendation === 'GOOD_FIT') || [];
      case 'moderate':
        return data.ranked_applications?.filter(a => a.ai_score?.recommendation === 'MODERATE_FIT') || [];
      case 'weak':
        return data.ranked_applications?.filter(a => a.ai_score?.recommendation === 'WEAK_FIT') || [];
      case 'unscored':
        return data.unscored_applications || [];
      default:
        return all;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <Loader className="animate-spin" size={24} />
          <span>Loading AI Dashboard...</span>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const job = data?.job || {};

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link 
                to="/company/jobs"
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={20} />
                <span className="hidden sm:inline">Back to Jobs</span>
              </Link>
              <div className="h-6 w-px bg-white/10"></div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Brain className="text-white" size={20} />
                </div>
                <div>
                  <h1 className="font-bold text-white">AI Scoring Dashboard</h1>
                  <p className="text-xs text-gray-500">{job.title}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleBulkScore}
                disabled={scoring || summary.unscored === 0}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg font-medium hover:from-purple-600 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bulk-score-btn"
              >
                {scoring ? (
                  <Loader className="animate-spin" size={18} />
                ) : (
                  <Sparkles size={18} />
                )}
                <span className="hidden sm:inline">
                  {scoring ? 'Scoring...' : `Score All (${summary.unscored || 0})`}
                </span>
              </button>
              
              <button
                onClick={() => fetchRankedApplications()}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-[#0a0a0a] rounded-xl border border-white/10 p-4" data-testid="stat-total">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Users className="text-blue-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{summary.total_applications || 0}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#0a0a0a] rounded-xl border border-white/10 p-4" data-testid="stat-scored">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Brain className="text-purple-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{summary.scored || 0}</p>
                <p className="text-xs text-gray-500">AI Scored</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#0a0a0a] rounded-xl border border-emerald-500/20 p-4" data-testid="stat-strong">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Star className="text-emerald-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-400">{summary.strong_fit || 0}</p>
                <p className="text-xs text-gray-500">Strong Fit</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#0a0a0a] rounded-xl border border-blue-500/20 p-4" data-testid="stat-good">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-blue-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-400">{summary.good_fit || 0}</p>
                <p className="text-xs text-gray-500">Good Fit</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#0a0a0a] rounded-xl border border-amber-500/20 p-4" data-testid="stat-moderate">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <AlertCircle className="text-amber-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{summary.moderate_fit || 0}</p>
                <p className="text-xs text-gray-500">Moderate</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#0a0a0a] rounded-xl border border-red-500/20 p-4" data-testid="stat-weak">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <XCircle className="text-red-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400">{summary.weak_fit || 0}</p>
                <p className="text-xs text-gray-500">Weak Fit</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="bg-[#0a0a0a] rounded-xl border border-white/10 p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAutoShortlist}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-lg font-medium hover:bg-emerald-500/30 transition-all"
                data-testid="auto-shortlist-btn"
              >
                <UserCheck size={18} />
                Auto-Shortlist Top
              </button>
              
              <button
                onClick={handleSelectAllStrongFit}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-gray-300 rounded-lg font-medium hover:bg-white/10 transition-all"
                data-testid="select-strong-btn"
              >
                <CheckSquare size={18} />
                Select Strong ({summary.strong_fit || 0})
              </button>

              <button
                onClick={handleCompare}
                disabled={selectedCandidates.length < 2 || selectedCandidates.length > 4}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-lg font-medium hover:bg-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="compare-btn"
              >
                <GitCompare size={18} />
                Compare ({selectedCandidates.length})
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              {selectedCandidates.length > 0 && (
                <span className="text-sm text-gray-400">
                  {selectedCandidates.length} selected
                </span>
              )}
              <button
                onClick={() => setShowScheduleModal(true)}
                disabled={selectedCandidates.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="schedule-interview-btn"
              >
                <CalendarPlus size={18} />
                Schedule Interview
              </button>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'all', label: 'All', count: summary.total_applications },
            { id: 'strong', label: 'Strong Fit', count: summary.strong_fit, color: 'emerald' },
            { id: 'good', label: 'Good Fit', count: summary.good_fit, color: 'blue' },
            { id: 'moderate', label: 'Moderate', count: summary.moderate_fit, color: 'amber' },
            { id: 'weak', label: 'Weak Fit', count: summary.weak_fit, color: 'red' },
            { id: 'unscored', label: 'Unscored', count: summary.unscored, color: 'gray' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                selectedTab === tab.id 
                  ? 'bg-white/10 text-white border border-white/20' 
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                tab.color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
                tab.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                tab.color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                tab.color === 'red' ? 'bg-red-500/20 text-red-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {tab.count || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Applications List */}
        <div className="space-y-4">
          {filteredApplications().length === 0 ? (
            <div className="bg-[#0a0a0a] rounded-xl border border-white/10 p-12 text-center">
              <Brain className="mx-auto text-gray-600 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-white mb-2">No Applications</h3>
              <p className="text-gray-500">
                {selectedTab === 'unscored' 
                  ? 'All applications have been scored!'
                  : 'No applications match this filter.'}
              </p>
            </div>
          ) : (
            filteredApplications().map((app, idx) => (
              <div 
                key={app.id || idx}
                className={`bg-[#0a0a0a] rounded-xl border transition-all ${
                  selectedCandidates.includes(app.id) 
                    ? 'border-cyan-500/50 bg-cyan-500/5' 
                    : 'border-white/10 hover:border-white/20'
                }`}
                data-testid={`application-card-${idx}`}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={() => handleSelectCandidate(app.id)}
                      className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        selectedCandidates.includes(app.id)
                          ? 'bg-cyan-500 border-cyan-500'
                          : 'border-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {selectedCandidates.includes(app.id) && (
                        <CheckCircle className="text-white" size={14} />
                      )}
                    </button>
                    
                    {/* Candidate Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-semibold text-white">
                            {app.student_name || 'Unknown'}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            {app.student_college || 'N/A'}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-sm text-gray-500">
                              CodeArena Score: <span className="text-cyan-400 font-medium">{app.student_score || 0}</span>
                            </span>
                            <span className="text-sm text-gray-500">
                              Rank: <span className="text-purple-400 font-medium">#{app.student_rank || 'N/A'}</span>
                            </span>
                            <span className="text-sm text-gray-500">
                              Status: <span className="text-amber-400 font-medium capitalize">{app.status || 'pending'}</span>
                            </span>
                          </div>
                        </div>
                        
                        {/* AI Score */}
                        {app.ai_score ? (
                          <div className="text-right">
                            <div className={`text-4xl font-bold ${getScoreColor(app.ai_score.overall_score)}`}>
                              {app.ai_score.overall_score}
                            </div>
                            <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold border ${getRecommendationColor(app.ai_score.recommendation)}`}>
                              {app.ai_score.recommendation?.replace('_', ' ')}
                            </span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleSingleScore(app.id)}
                            className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-500/30 transition-all flex items-center gap-2"
                          >
                            <Sparkles size={16} />
                            Score Now
                          </button>
                        )}
                      </div>
                      
                      {/* AI Insights */}
                      {app.ai_score && (
                        <div className="mt-4 pt-4 border-t border-white/5">
                          <div className="grid md:grid-cols-4 gap-4 mb-4">
                            <div className="bg-white/5 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">Skill Match</p>
                              <p className={`text-xl font-bold ${getScoreColor(app.ai_score.skill_match_score)}`}>
                                {app.ai_score.skill_match_score || 0}%
                              </p>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">Experience</p>
                              <p className={`text-xl font-bold ${getScoreColor(app.ai_score.experience_score || 0)}`}>
                                {app.ai_score.experience_score || 0}%
                              </p>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">Potential</p>
                              <p className={`text-xl font-bold ${getScoreColor(app.ai_score.potential_score || 0)}`}>
                                {app.ai_score.potential_score || 0}%
                              </p>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">Culture Fit</p>
                              <p className={`text-xl font-bold ${getScoreColor(app.ai_score.culture_fit_score || 0)}`}>
                                {app.ai_score.culture_fit_score || 0}%
                              </p>
                            </div>
                          </div>
                          
                          <p className="text-gray-400 text-sm mb-3">
                            {app.ai_score.summary}
                          </p>
                          
                          {app.ai_score.strengths?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              <span className="text-xs text-gray-500">Strengths:</span>
                              {app.ai_score.strengths.map((s, i) => (
                                <span key={i} className="px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded text-xs">
                                  {s}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {app.ai_score.gaps?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              <span className="text-xs text-gray-500">Gaps:</span>
                              {app.ai_score.gaps.map((g, i) => (
                                <span key={i} className="px-2 py-1 bg-amber-500/10 text-amber-400 rounded text-xs">
                                  {g}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {app.ai_score.interview_focus_areas?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              <span className="text-xs text-gray-500">Interview Focus:</span>
                              {app.ai_score.interview_focus_areas.map((a, i) => (
                                <span key={i} className="px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs">
                                  {a}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Cover Letter Preview */}
                      {app.cover_letter && (
                        <div className="mt-4 pt-4 border-t border-white/5">
                          <p className="text-xs text-gray-500 mb-1">Cover Letter</p>
                          <p className="text-gray-400 text-sm line-clamp-2">{app.cover_letter}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Interview Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0a] rounded-2xl border border-white/10 w-full max-w-lg">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Send className="text-cyan-400" size={24} />
                Send Interview Invites
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Sending to {selectedCandidates.length} candidate{selectedCandidates.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Interview Date</label>
                  <input
                    type="date"
                    value={inviteForm.interview_date}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, interview_date: e.target.value }))}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Interview Time</label>
                  <input
                    type="time"
                    value={inviteForm.interview_time}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, interview_time: e.target.value }))}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Interview Type</label>
                <select
                  value={inviteForm.interview_type}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, interview_type: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                >
                  <option value="video">Video Call</option>
                  <option value="phone">Phone Call</option>
                  <option value="onsite">On-site</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Additional Message (Optional)</label>
                <textarea
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Add a personalized message..."
                  rows={3}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500 resize-none"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-white/10 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowInviteModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvites}
                disabled={sendingInvites || !inviteForm.interview_date}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50"
                data-testid="confirm-send-invites-btn"
              >
                {sendingInvites ? (
                  <Loader className="animate-spin" size={18} />
                ) : (
                  <Send size={18} />
                )}
                Send Invites
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Comparison Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#0a0a0a] rounded-2xl border border-white/10 w-full max-w-5xl my-8">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <GitCompare className="text-purple-400" size={24} />
                  AI Candidate Comparison
                </h2>
                <p className="text-gray-400 text-sm mt-1">
                  Comparing {selectedCandidates.length} candidates
                </p>
              </div>
              <button
                onClick={() => setShowCompareModal(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {comparing ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader className="animate-spin text-purple-400 mb-4" size={48} />
                  <p className="text-gray-400">AI is analyzing candidates...</p>
                </div>
              ) : comparisonResult ? (
                <div className="space-y-6">
                  {/* Candidates Side by Side */}
                  <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${comparisonResult.candidates?.length || 2}, 1fr)` }}>
                    {comparisonResult.candidates?.map((candidate, idx) => (
                      <div key={idx} className={`bg-white/5 rounded-xl p-4 border ${
                        comparisonResult.recommendation?.top_pick === candidate.name 
                          ? 'border-emerald-500/50 ring-2 ring-emerald-500/20' 
                          : 'border-white/10'
                      }`}>
                        {comparisonResult.recommendation?.top_pick === candidate.name && (
                          <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium mb-3">
                            <Trophy size={16} />
                            Top Recommendation
                          </div>
                        )}
                        <h3 className="font-bold text-white text-lg">{candidate.name}</h3>
                        <p className="text-gray-400 text-sm mb-3">{candidate.college}</p>
                        
                        <div className="flex items-center gap-4 mb-4">
                          <div className={`text-3xl font-bold ${getScoreColor(candidate.overall_score)}`}>
                            {candidate.overall_score}
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getRecommendationColor(candidate.recommendation)}`}>
                            {candidate.recommendation?.replace('_', ' ')}
                          </span>
                        </div>
                        
                        {candidate.key_strengths?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                              <ThumbsUp size={12} className="text-emerald-400" />
                              Strengths
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {candidate.key_strengths.map((s, i) => (
                                <span key={i} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-xs">
                                  {s}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {candidate.key_weaknesses?.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                              <ThumbsDown size={12} className="text-red-400" />
                              Areas of Concern
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {candidate.key_weaknesses.map((w, i) => (
                                <span key={i} className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded text-xs">
                                  {w}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* AI Analysis */}
                  {comparisonResult.analysis && (
                    <div className="bg-purple-500/10 rounded-xl p-5 border border-purple-500/20">
                      <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                        <Brain className="text-purple-400" size={20} />
                        AI Analysis
                      </h4>
                      <p className="text-gray-300 leading-relaxed">{comparisonResult.analysis}</p>
                    </div>
                  )}

                  {/* Recommendation */}
                  {comparisonResult.recommendation && (
                    <div className="bg-emerald-500/10 rounded-xl p-5 border border-emerald-500/20">
                      <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                        <Trophy className="text-emerald-400" size={20} />
                        Final Recommendation
                      </h4>
                      <p className="text-gray-300 mb-3">{comparisonResult.recommendation.reasoning}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-emerald-400 font-bold">
                          Proceed with: {comparisonResult.recommendation.top_pick}
                        </span>
                        {comparisonResult.recommendation.confidence && (
                          <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                            {comparisonResult.recommendation.confidence}% confident
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No comparison data available
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-white/10 flex items-center justify-end">
              <button
                onClick={() => setShowCompareModal(false)}
                className="px-6 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Interview Scheduling Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#0a0a0a] rounded-2xl border border-white/10 w-full max-w-xl my-8">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <CalendarPlus className="text-cyan-400" size={24} />
                Schedule Interview
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Scheduling for {selectedCandidates.length} candidate{selectedCandidates.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Selected Candidates Preview */}
              <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <p className="text-xs text-gray-500 mb-2">Selected Candidates:</p>
                <div className="flex flex-wrap gap-2">
                  {getSelectedCandidatesData().map((c, i) => (
                    <span key={i} className="px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded text-sm">
                      {c.student_name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Date *</label>
                  <input
                    type="date"
                    value={inviteForm.interview_date}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, interview_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Time *</label>
                  <input
                    type="time"
                    value={inviteForm.interview_time}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, interview_time: e.target.value }))}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Duration</label>
                  <select
                    value={inviteForm.interview_duration}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, interview_duration: e.target.value }))}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  >
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Interview Type</label>
                  <select
                    value={inviteForm.interview_type}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, interview_type: e.target.value }))}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  >
                    <option value="video">📹 Video Call</option>
                    <option value="phone">📞 Phone Call</option>
                    <option value="onsite">📍 On-site</option>
                  </select>
                </div>
              </div>

              {inviteForm.interview_type === 'video' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Meeting Link (Optional)</label>
                  <input
                    type="url"
                    value={inviteForm.meeting_link}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, meeting_link: e.target.value }))}
                    placeholder="https://meet.google.com/... or Zoom link"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  />
                </div>
              )}

              {inviteForm.interview_type === 'onsite' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Location</label>
                  <input
                    type="text"
                    value={inviteForm.location}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Office address..."
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Message to Candidates</label>
                <textarea
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Add interview instructions, what to prepare..."
                  rows={3}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500 resize-none"
                />
              </div>

              {/* Calendar Invite Option */}
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                <input
                  type="checkbox"
                  id="calendar_invite"
                  checked={inviteForm.send_calendar_invite}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, send_calendar_invite: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                />
                <label htmlFor="calendar_invite" className="flex-1">
                  <span className="text-white font-medium">Send Calendar Invite (.ics)</span>
                  <p className="text-gray-500 text-xs">Candidates can add to Google Calendar, Outlook, or Apple Calendar</p>
                </label>
                <Calendar className="text-cyan-400" size={20} />
              </div>
            </div>
            
            <div className="p-6 border-t border-white/10 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleInterview}
                disabled={sendingInvites || !inviteForm.interview_date || !inviteForm.interview_time}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50"
                data-testid="confirm-schedule-btn"
              >
                {sendingInvites ? (
                  <Loader className="animate-spin" size={18} />
                ) : (
                  <CalendarPlus size={18} />
                )}
                Schedule & Send Invites
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIScoreDashboard;
